/**
 * Pembacaan angka uang untuk halaman Laporan Toko.
 *
 * ERP dan backend adalah dua deployment terpisah, jadi frontend bisa naik lebih
 * dulu dan menerima baris TANPA field turunan yang baru. `Number(undefined)`
 * menghasilkan NaN, dan satu baris cacat cukup untuk mengubah seluruh kolom uang
 * menjadi "Rp NaN". Karena itu setiap kolom uang di laporan dibaca lewat berkas
 * ini, bukan lewat `Number(row.x)` langsung.
 *
 * Sengaja satu berkas: sebelumnya `angka()` tersalin di lima halaman laporan,
 * dan salinan di jalur uang adalah cara termudah membuat satu halaman
 * menampilkan angka yang berbeda dari halaman lain.
 *
 * KONTRAK BACKEND yang dipakai jalur cadangan di bawah (diverifikasi di
 * `backend-vone/src/services/pos/PosTransactionService.js`):
 *
 *   - `discount` HANYA diskon per baris: `headerTotalsFromLines` menjumlahkannya
 *     sebagai `discount_price × qty` (baris 67), dan `close()` mempersistnya
 *     terpisah dari `global_discount_amount` (baris 1309 dan 1316). Jadi
 *     menjumlahkan keduanya TIDAK menghitung diskon global dua kali.
 *   - `grand_total` TIDAK memuat service charge, ongkir, maupun diskon global:
 *     `grandTotal = ceil(subTotal − discountTotal + taxTotal)` (baris 83).
 *     Ketiganya disimpan sebagai kolom terpisah dan baru dijumlahkan oleh
 *     `effectiveBill` (baris 108) — rumus yang disalin ke `tagihanEfektif` di
 *     bawah.
 */

/**
 * Baca angka uang dengan aman. Kolom DECIMAL datang dari backend sebagai STRING
 * ("17000000.00"), dan field yang absen harus terbaca 0, bukan NaN.
 */
export const angka = (v) => Number(v ?? 0) || 0;

/**
 * TAGIHAN EFEKTIF — angka yang benar-benar dibayar pelanggan:
 *
 *     grand_total − global_discount_amount + service_charge + delivery_fee
 *
 * Dipakai field turunan `amount_due` dari backend bila ada. Kalau tidak ada,
 * rumusnya dihitung ulang di sini — BUKAN jatuh ke `grand_total` mentah, yang
 * akan melaporkan kurang secara diam-diam (kehilangan ongkir, service charge,
 * dan diskon global).
 */
export const tagihanEfektif = (row) =>
  row?.amount_due !== undefined && row?.amount_due !== null
    ? angka(row.amount_due)
    : Math.max(
        0,
        angka(row?.grand_total) -
          angka(row?.global_discount_amount) +
          angka(row?.service_charge) +
          angka(row?.delivery_fee)
      );

/**
 * TOTAL DISKON yang dilihat pelanggan: diskon per baris + diskon global.
 *
 * `discount` sendiri bukan total diskon — diskon global disimpan di kolom
 * terpisah, jadi laporan yang menampilkan `discount` saja melaporkan potongan
 * yang lebih kecil daripada yang diterima pelanggan.
 */
export const totalDiskon = (row) =>
  row?.total_discount !== undefined && row?.total_discount !== null
    ? angka(row.total_discount)
    : angka(row?.discount) + angka(row?.global_discount_amount);
